<template>
  <v-expansion-panels>
    <hb-expansion-panel>
      <template v-slot:title> Report Configuration </template>

      <template v-slot:content>
        <hb-form
          v-if="!isEditMode"
          label="Select Reports"
          full
        >
          <v-row
            no-gutters
            v-for="item in reportConfigurations"
            :key="item.id"
          >
            <span
              class="hb-font-body d-flex align-center justify-space-between text-capitalize"
            >
              {{ getReportName(item.saved_report_id) }} -
              {{ item.file_type?.toUpperCase() }}
            </span>
          </v-row>
        </hb-form>

        <hb-form
          v-if="!isEditMode"
          label="Select Exports"
          full
        >
          <v-row
            no-gutters
            v-for="item in accountingExportsConfigurations"
            :key="item.id"
          >
            <span
              class="hb-font-body d-flex align-center justify-space-between text-capitalize"
            >
              {{
                ACCOUNTING.FORMAT_LIST.find(
                  (f) => f.value == item.export_format
                ).text
              }}
            </span>
          </v-row>
        </hb-form>
        <hb-form
          v-if="!isEditMode"
          label="Report Format"
        >
          <v-row no-gutters>
            <span
              class="hb-font-body d-flex align-center justify-space-between text-capitalize"
            >
              By {{ format }}
            </span>
          </v-row>
        </hb-form>
        <hb-form
          v-if="!isEditMode"
          label="Select Recipients"
        >
          <hb-chip
            v-for="(item, i) in selectedRecipients"
            :key="'combobox4' + i"
            display
          >
            {{ item.name }}
          </hb-chip>
        </hb-form>

        <!-- reports edit mode -->

        <hb-form
          v-if="isEditMode"
          label="Select Reports"
          description="Configure which reports are required after the month ends."
          full
        >
          <v-row
            no-gutters
            v-for="item in reportConfigurations"
            :key="item.id"
            class="mb-1"
          >
            <v-col
              cols="6"
              class="pr-1"
            >
              <span v-if="item.saved_report_id || item.file_type" class="hb-text-light hb-font-caption">Report</span>
              <HbSelect
                class="pt-1"
                v-model="item.saved_report_id"
                :items="filteredReportTypes"
                item-text="name"
                item-value="id"
                v-validate="'required'"
                :data-vv-name="'report' + item.id"
                data-vv-as="Report"
                :error="errors.has('report' + item.id)"
                placeholder="Report"
                :clearable="false"
              />
            </v-col>
            <v-col cols="6">
              <span v-if="item.saved_report_id || item.file_type" class="hb-text-light hb-font-caption">File Type</span>
              <HbSelect
                class="pt-1"
                :items="getFileTypesForReport(item)"
                :disabled="!item.saved_report_id"
                item-text="label"
                item-value="value"
                data-vv-as="File Type"
                :data-vv-name="'fileType' + item.id"
                v-validate="'required'"
                :error="errors.has('fileType' + item.id)"
                v-model="item.file_type"
                placeholder="File Type"
                :full="true"
                :clearable="false"
              >
                <template v-slot:selection="{ item }">
                  <span class="mr-1">{{ item.label }}</span>
                </template>
              </HbSelect>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <span
              class="primary--text"
              @click="addNewReport"
              style="cursor: pointer"
              ><v-icon
                color="primary"
                size="small"
                >mdi-plus</v-icon
              >Add Report</span
            >
          </v-row>
        </hb-form>

        <hb-form
          v-if="isEditMode"
          label="Select Exports"
          description="Configure which Exports are required after the month ends."
          full
        >
          <v-row
            no-gutters
            v-for="item in accountingExportsConfigurations"
            :key="item.id"
            class="mb-1"
          >
            <v-col
              cols="6"
              class="pb-1"
            >
              <span v-if="item.export_format" class="hb-text-light hb-font-caption">Export</span>
              <HbSelect
                v-model="item.export_format"
                :items="exportFormatList"
                item-text="text"
                item-value="value"
                v-validate="'required'"
                id="exportFileType"
                name="Export File Type"
                data-vv-as="Export File Type"
                placeholder="Choose Export"
                :data-vv-name="'exportFileType' + item.id"
                :error="errors.has('exportFileType' + item.id)"
                class="text-capitalize pb-1 pt-1"
                :clearable="false"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <span
              class="primary--text"
              @click="addNewExport"
              style="cursor: pointer"
              ><v-icon
                color="primary"
                size="small"
                >mdi-plus</v-icon
              >Add Export</span
            >
          </v-row>
        </hb-form>

        <hb-form
          v-if="isEditMode"
          label="Report format"
          description="Select Report format."
        >
          <hb-radio-group
            row
            v-model="format"
            :error="!!errors.collect('reportFormat').length > 0"
            hide-details
            @change="updateReportFormat"
          >
            <hb-radio
              value="property"
              label="by Property"
            ></hb-radio>
            <hb-radio
              value="report"
              label="by Report"
            ></hb-radio>
          </hb-radio-group>
        </hb-form>
        <hb-form
          v-if="isEditMode"
          label="Select Recipients"
          description="Select all recipients that should receive these reports."
        >
          <hb-combobox
            v-model="selectedRecipients"
            :items="recipientsList"
            item-text="first"
            return-object
            label="Recipients"
          ></hb-combobox>
        </hb-form>
      </template>
      <template v-slot:footer>
        <hb-bottom-action-bar
          cancel-off
          :top-border="false"
        >
          <template v-slot:right-actions>
            <template v-if="!hasPermission('edit_eom_configuration')">
            <hb-tooltip>
                <template v-slot:item>
                  <hb-btn
                    :disabled=true
                    v-if="!isEditMode"
                    color="secondary"
                    >Edit</hb-btn
                  >                
                </template>
                <template v-slot:body>
                  You do not have permission to edit EOM configurations.
                </template>
            </hb-tooltip>
        </template>
        <template v-else>
          <hb-btn
              v-if="!isEditMode"
              color="secondary"
              @click="() => (isEditMode = true)"
              >Edit</hb-btn
            >
        </template>
            <hb-link
              v-if="isEditMode"
              @click="cancel"
              >Cancel</hb-link
            >
            <hb-btn
              v-if="isEditMode"
              color="primary"
              small
              @click="saveConfigurations"
              >Save</hb-btn
            >
          </template>
        </hb-bottom-action-bar>
      </template>
    </hb-expansion-panel>
  </v-expansion-panels>
</template>

<script type="text/babel">
import api from "../../assets/api.js";
import { ACCOUNTING } from "@/constants/accounting.js";
import { mapGetters } from "vuex";
import { notificationMixin } from '../../mixins/notificationMixin.js';
export default {
  name: "ReportConfiguration",
  mixins: [notificationMixin],
  data() {
    return {
      isEditMode: false,
      fileTypes: [
        { label: "PDF", value: "pdf" },
        { label: "XLSX", value: "xlsx" },
      ],
      format: "",
      reportTypes: [],
      selectedRecipients: [],
      recipientsList: [],
      reports: this.reportsData,
      filteredReportTypes: [],
      recipients: this.recipientsData,
      reportConfigurations: [],
      accountingExportsConfigurations: [],
      exportFormatList: ACCOUNTING.FORMAT_LIST,
      ACCOUNTING,
    };
  },
  mixins: [ notificationMixin ],
  props: ["reportsData", "recipientsData", "historyData"],
  computed: {
    ...mapGetters({
      hasPermission: "authenticationStore/rolePermission",
    }),
  },
  components: {},
  mounted() {
    if (!this.hasPermission("view_end_of_month_configuration"))
      this.$router.push("/dashboard");
  },
  async created() {
    this.ACCOUNTING = ACCOUNTING;
    this.exportFormatList = ACCOUNTING.FORMAT_LIST;
    await this.fetchReportsList();
    await this.fetchAdmins();
    await this.fetchConfigurations();
  },
  watch: {
    reportConfigurations: {
      handler() {
        this.filterReportTypes();
      },
      immediate: true,
      deep: true,
    },
    accountingExportsConfigurations: {
      handler() {
        this.filterExportTypes();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    filterReportTypes() {
      this.filteredReportTypes = this.reportTypes;
    },
    filterExportTypes() {
      this.exportFormatList = ACCOUNTING.FORMAT_LIST;
    },
    getFileTypesForReport(item) {
      const foundReportType = this.reportTypes.find(
        (x) => x.id === item.saved_report_id
      );
      let fileTypes = null;
      if (foundReportType) {
        const pdf = {
          label: "PDF",
          value: "pdf",
        };
        const xlsx = {
          label: "XLSX",
          value: "xlsx",
        };
        fileTypes = [];
        if(foundReportType.download_pdf){
          fileTypes.push(pdf);
        }
        if(foundReportType.download_xls){
          fileTypes.push(xlsx);
        }
      }
      return fileTypes ? fileTypes : this.fileTypes;
    },
    cancel() {
      this.isEditMode = false;
      this.fetchConfigurations();
    },
    updateReportFormat(param) {
      this.format = param;
    },
    addNewReport() {
      const length = this.reportConfigurations?.length - 1;
      if (length >= 0 && !this.reportConfigurations[length]?.saved_report_id) {
        return false;
      }
      this.reportConfigurations.push({
        saved_report_id: null,
      });
    },
    addNewExport() {
      const length = this.accountingExportsConfigurations?.length - 1;
      if (
        length >= 0 &&
        !this.accountingExportsConfigurations[length]?.export_format
      ) {
        return false;
      }
      this.accountingExportsConfigurations.push({
        export_format: null,
      });
    },
    getReportName(report_id) {
      const report = this.reportTypes.find((x) => x.id === report_id);
      return report ? report.name : report_id;
    },
    async fetchAdmins() {
      await api.get(this, api.ADMIN).then((r) => {
        this.recipientsList = r.admins.map((admin) => {
          return {
            email: admin?.email,
            first: admin?.first,
            last: admin?.last,
            name: admin?.first + " " + admin?.last,
          };
        });
      });
    },
    async fetchReportsList() {
      const reports = await api.get(this, api.END_OF_MONTH_REPORTS);
      this.reportTypes = reports?.end_of_month_reports;
    },
    async fetchConfigurations() {
      try{
        await api.get(this, api.END_OF_MONTH_CONFIG).then((r) => {
          const {
            accountingExportsConfigurations = [],
            reportConfigurations = [],
            format = "property",
            send_to = [],
          } = r?.end_of_month_config;
          this.accountingExportsConfigurations = accountingExportsConfigurations;
          this.reportConfigurations = reportConfigurations;
          this.format = format;
          this.selectedRecipients = send_to;
        });
      }
      catch (err) {
        this.showMessageNotification({
          type: "error",
          description: err,
        });
      }
    },
    async saveConfigurations() {
      try {
        let status = await this.$validator.validateAll();
        if (!status) {
          this.showMessageNotification({
            type: "error",
            description:
              "There are errors in your form, correct them before continuing.",
          });
          return;
        }
        const data = {
          format: this.format,
          send_to: this.selectedRecipients,
          report_configurations: this.reportConfigurations,
          accounting_exports_configurations:
            this.accountingExportsConfigurations,
        };
        await api.put(this, api.END_OF_MONTH_CONFIG, data);
        this.isEditMode = false;
        this.showMessageNotification({
          type: "success",
          description: "Configurations updated successfully.",
        });
        this.fetchConfigurations();
      } catch (err) {
        this.showMessageNotification({
          type: "error",
          description: err,
        });
      }
    },
  },
};
</script>
<style scoped>
.hb-select-width-370 {
  max-width: 97% !important;
}
</style>
